import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 2 }

import { computed, ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { CouponUpdateDocument, CouponType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { formatDate, formatDateStringToIso } from '@/utils/date-utils';
import { capitalizeFirstLetter } from '@/utils/string-utils';

// --- Component properties ---

export default /*@__PURE__*/_defineComponent({
  __name: 'CouponUpdate',
  props: {
    coupon: {},
    show: { type: Boolean }
  },
  emits: ['close', 'closeAndRefresh'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// --- Component emits ---
const emits = __emit;

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});
const newUnitsRemaining = ref<number | null>(
  props.coupon.unitsRemaining || null
);
const newExpiresAt = ref<Date | null>(props.coupon.expiresAt);
const clearExpirationDate = ref<boolean>(props.coupon.expiresAt !== null);

// --- Mutation ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(CouponUpdateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.couponUpdate) {
    console.debug('Coupon updated:', res.data.couponUpdate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Coupon update failed: ' + res.data;
    console.error('Coupon update failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error updating coupon:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function close() {
  emits('close');
}

function updateRequested() {
  console.debug('Update requested');

  if (!props.coupon.id) {
    mutationError.value = 'Coupon ID is missing';
    console.error('Coupon ID is missing');
    return;
  }

  let expirationDate: string | null = null;

  if (!clearExpirationDate.value) {
    expirationDate = formatDateStringToIso(newExpiresAt.value);
  }

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    id: props.coupon.id,
    unitsRemaining: newUnitsRemaining.value,
    expiresAt: expirationDate,
    clearExpirationDate: clearExpirationDate.value,
  });
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BModal), {
      modelValue: show.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((show).value = $event)),
      onHide: close
    }, {
      title: _withCtx(() => [
        _createVNode(_component_font_awesome_icon, {
          icon: "ticket",
          class: "me-2"
        }),
        _createTextVNode(" " + _toDisplayString(_unref(capitalizeFirstLetter)(__props.coupon.couponType)) + " coupon ", 1)
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          onClick: close,
          variant: "secondary"
        }, {
          default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
            _createTextVNode(" Close ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_unref(BButton), {
          onClick: updateRequested,
          disabled: mutationInProgress.value || mutationShowSuccess.value,
          variant: "primary"
        }, {
          default: _withCtx(() => [
            (mutationInProgress.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
                  _cache[11] || (_cache[11] = _createTextVNode(" Updating Coupon ", -1)),
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    label: ""
                  })
                ]))
              : (mutationShowSuccess.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
                    _cache[12] || (_cache[12] = _createTextVNode(" Coupon Updated ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fa', 'check-double'],
                      class: "me-2"
                    })
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_3, " Update Coupon "))
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _createElementVNode("p", null, [
            _cache[6] || (_cache[6] = _createElementVNode("strong", null, "Current status: ", -1)),
            _createTextVNode(" " + _toDisplayString(__props.coupon.unitsRemaining) + " of " + _toDisplayString(__props.coupon.unitsTotal) + " " + _toDisplayString(__props.coupon.units) + " remaining", 1),
            _cache[7] || (_cache[7] = _createElementVNode("br", null, null, -1)),
            _cache[8] || (_cache[8] = _createElementVNode("strong", null, "Expires: ", -1)),
            _createTextVNode(" " + _toDisplayString(_unref(formatDate)(__props.coupon.expiresAt, 'no expiration')), 1)
          ]),
          _createVNode(_unref(BFormGroup), {
            label: `${_unref(capitalizeFirstLetter)(__props.coupon.units)} remaining`,
            "label-for": "new-units-remaining",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: newUnitsRemaining.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newUnitsRemaining).value = $event)),
                modelModifiers: { number: true },
                id: "new-units-remaining",
                name: "new-units-remaining",
                type: "number",
                placeholder: "Enter the number of units remaining",
                min: "0"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }, 8, ["label"]),
          _createVNode(_unref(BFormGroup), {
            label: "Expiration date",
            "label-for": "new-expiration-date",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: newExpiresAt.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newExpiresAt).value = $event)),
                id: "new-expiration-date",
                name: "new-expiration-date",
                placeholder: "Enter a new expiration date",
                type: "date",
                disabled: clearExpirationDate.value
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormCheckbox), {
                modelValue: clearExpirationDate.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((clearExpirationDate).value = $event)),
                id: "clear-expiration-date",
                name: "clear-expiration-date"
              }, {
                default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                  _createTextVNode(" Clear expiration date ", -1)
                ]))]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BAlert), {
            modelValue: mutationError.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((mutationError).value = $event)),
            variant: "danger",
            dismissible: "",
            class: "mt-3",
            onDismissed: _cache[4] || (_cache[4] = ($event: any) => (mutationError.value = null))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(mutationError.value), 1)
            ]),
            _: 1
          }, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})